import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import {
  useTravelEsimDestinations,
  useTravelEsimQuote,
} from '@/hooks/useTravelEsim';
import { upcomingStay } from '@/lib/travel-esim';
import { ChevronDown, Info, Check } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TravelEsimOrderDialog } from './TravelEsimOrderDialog';
import { QuotePanel } from './TravelEsimQuotePanel';
import { DestinationPicker, StayPicker } from './TravelEsimSearchFields';
import { TravelEsimTutorialDialog } from './TravelEsimTutorialDialog';

const PARAM_DESTINATION = 'destination';
const PARAM_FROM = 'from';
const PARAM_TO = 'to';
/** Line to pre-attach, set by the line detail's "Commander une eSIM" entry. */
const PARAM_LINE = 'line';

/**
 * Catalog tab of the international eSIM, laid out as a configurator: the
 * destination and the stay on the left, the one offer priced by the server
 * for that stay on the right, refreshed live. Both dates are required. The
 * search lives in the URL so a link (or the line detail) can land on it.
 */
export function TravelEsimTab() {
  const { data: organization } = useCurrentOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ordering, setOrdering] = useState(false);

  const initialLineId = searchParams.get(PARAM_LINE) ?? undefined;
  const destination = searchParams.get(PARAM_DESTINATION) ?? undefined;
  const { departureDate, returnDate } = upcomingStay(
    searchParams.get(PARAM_FROM) ?? undefined,
    searchParams.get(PARAM_TO) ?? undefined,
  );

  const updateSearch = (
    changes: Partial<Record<'destination' | 'from' | 'to', string | undefined>>,
  ) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
    setOrdering(false);
  };

  const { data: destinations, isLoading: destinationsLoading } =
    useTravelEsimDestinations(organization?.id);
  const { data: quote, isFetching: quoteFetching } = useTravelEsimQuote(
    organization?.id,
    destination,
    departureDate,
    returnDate,
  );
  const ready = !!destination && !!departureDate && !!returnDate;

  return (
    <div className="grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
      <div className="space-y-6">
        <div className="rounded-2xl bg-white shadow-sm p-6 space-y-6">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Où partez-vous ?
            </h2>
            <p className="text-sm text-slate-500">
              Une eSIM data pour toute la durée de votre voyage.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Destination
              </label>
              <DestinationPicker
                destinations={destinations ?? []}
                isLoading={destinationsLoading}
                value={destination}
                onChange={(value) => updateSearch({ destination: value })}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Dates du séjour
              </label>
              <StayPicker
                departureDate={departureDate}
                returnDate={returnDate}
                onChange={(range) =>
                  updateSearch({ from: range.from, to: range.to })
                }
              />
            </div>
          </div>
        </div>

        <HowItWorks />
      </div>

      <aside className="lg:sticky lg:top-6">
        <QuotePanel
          destination={destination}
          ready={ready}
          loading={quoteFetching && quote === undefined}
          quote={ready ? (quote ?? null) : null}
          onOrder={() => setOrdering(true)}
        />
        {ordering && quote && (
          // Keyed and conditionally mounted: each quote gets a fresh modal
          // (form, chosen line, "added" state).
          <TravelEsimOrderDialog
            key={`${quote.country}-${quote.departureDate}-${quote.returnDate}`}
            quote={quote}
            initialLineId={initialLineId}
            onClose={() => setOrdering(false)}
          />
        )}
      </aside>
    </div>
  );
}

const GOOD_TO_KNOW = [
  'L’eSIM peut être installée dès réception : le forfait ne démarre qu’une fois le téléphone connecté au réseau du pays de destination.',
  'Une eSIM est réutilisable à l’infini. Elle est offerte à la première commande, puis se recharge autant de fois que souhaité.',
  'Conservez l’eSIM sur le téléphone pour les prochains déplacements. En cas de suppression, le même QR code se scanne à nouveau ; il reste disponible depuis la fiche de l’utilisateur.',
  'Les forfaits sont pré-chargés sur l’eSIM : un déplacement peut se planifier plusieurs jours à l’avance, et plusieurs forfaits peuvent être positionnés sur la même eSIM.',
  'Avant de commander, vérifiez que le téléphone est compatible eSIM. Au moment du scan, l’utilisateur doit être connecté au wifi.',
  'Les eSIM sont exclusivement destinées à un usage Internet.',
];

function HowItWorks() {
  const [showTutorial, setShowTutorial] = useState(false);
  const tutoLink = (
    <button
      type="button"
      onClick={() => setShowTutorial(true)}
      className="font-medium text-emerald-700 underline underline-offset-2 cursor-pointer"
    >
      tuto
    </button>
  );
  const steps = [
    'Choisissez la destination et vos dates de voyage : votre eSIM couvre le séjour du départ au retour.',
    'Rattachez l’eSIM à un membre de la flotte ou à un bénéficiaire hors flotte, puis validez la commande depuis le panier.',
    <>
      Le bénéficiaire reçoit le QR code par e-mail et l’installe sur son
      téléphone avant le départ, grâce au {tutoLink}.
    </>,
  ];
  return (
    <Collapsible className="rounded-xl border border-slate-200">
      <CollapsibleTrigger className="group flex w-full items-center gap-3 px-4 py-3 text-left cursor-pointer">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
          <Info className="h-3.5 w-3.5" />
        </span>
        <span className="flex-1 text-sm font-medium text-slate-900">
          Informations · comment fonctionne l'eSIM
        </span>
        <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 space-y-4">
        <ol className="space-y-2">
          {steps.map((step, index) => (
            <li key={index} className="flex items-start gap-3 text-sm text-slate-600">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
        <div>
          <p className="mb-2 text-sm font-medium text-slate-900">Bon à savoir</p>
          <ul className="space-y-1.5">
            {GOOD_TO_KNOW.map((point) => (
              <li key={point} className="flex items-start gap-2 text-sm text-slate-600">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      </CollapsibleContent>
      {showTutorial && (
        <TravelEsimTutorialDialog onClose={() => setShowTutorial(false)} />
      )}
    </Collapsible>
  );
}

